/**
 * yoloCapture — opt-in dataset capture for the Shot Analyzer.
 *
 * Captures up to 5 key frames per shot (release / mid / apex / descent /
 * result), packages them with the detections we already have (ball bbox
 * from HSV tracker, player bbox derived from BlazePose keypoint extremes,
 * rim from HoughCircles), and POSTs them to /api/yolo/capture.
 *
 * Frontend gating: respects `localStorage.pm_yolo_consent` ('0' = opted
 * out). The backend re-checks via the user's `yolo_consent` field.
 *
 * Failures are silent — this should NEVER block the user's main flow.
 */
import { api } from '@/lib/api';

/** Get a single JPEG snapshot of the current video frame as base64. */
function snapshot(videoEl, quality = 0.85) {
  if (!videoEl || videoEl.readyState < 2) return null;
  const canvas = document.createElement('canvas');
  // Downsize so we stay under the 2MB cap and below the 12-frame limit.
  const targetW = Math.min(960, videoEl.videoWidth || 640);
  const scale = targetW / (videoEl.videoWidth || targetW);
  canvas.width = targetW;
  canvas.height = Math.round((videoEl.videoHeight || 540) * scale);
  canvas.getContext('2d').drawImage(videoEl, 0, 0, canvas.width, canvas.height);
  return {
    dataUrl: canvas.toDataURL('image/jpeg', quality),
    w: canvas.width,
    h: canvas.height,
  };
}

/** Build a normalized player bbox from BlazePose 33 keypoints. */
function playerBboxFromKeypoints(landmarks) {
  if (!landmarks || landmarks.length === 0) return null;
  let minX = 1, maxX = 0, minY = 1, maxY = 0;
  for (const lm of landmarks) {
    if (lm.visibility != null && lm.visibility < 0.3) continue;
    if (lm.x < minX) minX = lm.x;
    if (lm.x > maxX) maxX = lm.x;
    if (lm.y < minY) minY = lm.y;
    if (lm.y > maxY) maxY = lm.y;
  }
  if (maxX <= minX || maxY <= minY) return null;
  // Add 6% padding so the bbox isn't tight to the keypoint extremes.
  const pad = 0.06;
  const w = Math.min(1, (maxX - minX) + pad * 2);
  const h = Math.min(1, (maxY - minY) + pad * 2);
  return {
    cx: +(((minX + maxX) / 2)).toFixed(4),
    cy: +(((minY + maxY) / 2)).toFixed(4),
    w: +w.toFixed(4),
    h: +h.toFixed(4),
  };
}

/** Build a ball bbox from a {x, y, r} ball sample. */
function ballBboxFromSample(sample) {
  if (!sample) return null;
  const r = Math.max(sample.r || 0.015, 0.01);
  return {
    cx: +sample.x.toFixed(4),
    cy: +sample.y.toFixed(4),
    w: +(r * 2).toFixed(4),
    h: +(r * 2).toFixed(4),
  };
}

function hoopBbox(rimPoint) {
  if (!rimPoint) return null;
  // Use a fixed 8% box centered on the calibrated rim point.
  return {
    cx: +rimPoint.x.toFixed(4),
    cy: +rimPoint.y.toFixed(4),
    w: 0.08,
    h: 0.08,
  };
}

export function hasConsent() {
  const v = localStorage.getItem('pm_yolo_consent');
  return v !== '0';
}

export function setConsent(on) {
  localStorage.setItem('pm_yolo_consent', on ? '1' : '0');
}

/**
 * Capture and POST the dataset for one shot.
 * @param {object} args
 *   - videoEl: HTMLVideoElement (must still be active)
 *   - ballSamples: array of {x,y,r,ts} from BallTracker
 *   - landmarksAtRelease: latest BlazePose snapshot (33 keypoints)
 *   - rimPoint: {x,y} or null
 *   - result: 'make' | 'miss' | 'unknown'
 *   - shotId: stable id (use the persisted attempt.id)
 *   - sessionId, courtZone, distanceNorm, angleDeg, fps, deviceLabel
 */
export async function captureAndUpload({
  videoEl, ballSamples = [], landmarksAtRelease, rimPoint, result = 'unknown',
  shotId, sessionId = null, courtZone = null, distanceNorm = null, angleDeg = null,
  fps = null, deviceLabel = null, trajectory = [],
}) {
  if (!hasConsent() || !videoEl) return { skipped: true };

  // Pick representative samples to label: release (1st), apex (lowest y),
  // descent (between apex and last), result (last).
  const sorted = [...ballSamples].sort((a, b) => a.ts - b.ts);
  const apex = sorted.reduce((best, s) => (s.y < best.y ? s : best), sorted[0] || { y: 1 });
  const mid = sorted[Math.floor(sorted.length / 3)] || null;
  const descent = sorted[Math.floor(sorted.length * 0.75)] || null;
  const last = sorted[sorted.length - 1] || null;
  const rebound = sorted.length > 0 && last && (last.ts - apex.ts) > 600
    ? 'offensive'  // ball still tracked >600ms after apex → likely a rebound situation
    : 'none';

  const now = Date.now();
  // Single live snapshot (current frame). For ball-only labels we re-use
  // the same image and re-label with the sampled bbox — cheap and useful.
  const snap = snapshot(videoEl);
  if (!snap) return { skipped: true, reason: 'no snapshot' };

  // Build per-role frames. For roles where we don't have a distinct visual
  // (we only have one current snapshot), we still send the same image with
  // a different label set so the dataset has temporal variety.
  const baseImg = snap.dataUrl;
  const playerBox = playerBboxFromKeypoints(landmarksAtRelease);
  const rimBox = hoopBbox(rimPoint);

  const frames = [];
  if (sorted[0]) frames.push({
    frame_image: baseImg,
    ts_ms: 0,
    role: 'release',
    ball: ballBboxFromSample(sorted[0]),
    player: playerBox,
    hoop: rimBox,
  });
  if (mid) frames.push({
    frame_image: baseImg,
    ts_ms: Math.round(mid.ts - sorted[0].ts),
    role: 'mid',
    ball: ballBboxFromSample(mid),
    hoop: rimBox,
  });
  if (apex) frames.push({
    frame_image: baseImg,
    ts_ms: Math.round(apex.ts - sorted[0].ts),
    role: 'apex',
    ball: ballBboxFromSample(apex),
    hoop: rimBox,
  });
  if (descent) frames.push({
    frame_image: baseImg,
    ts_ms: Math.round(descent.ts - sorted[0].ts),
    role: 'descent',
    ball: ballBboxFromSample(descent),
    hoop: rimBox,
  });
  if (last) frames.push({
    frame_image: baseImg,
    ts_ms: Math.round(last.ts - sorted[0].ts),
    role: 'result',
    ball: ballBboxFromSample(last),
    hoop: rimBox,
  });

  if (frames.length === 0) return { skipped: true, reason: 'no frames' };

  const body = {
    shot_id: shotId || `shot-${now}`,
    session_id: sessionId,
    frames,
    trajectory,
    result,
    rebound,
    court_zone: courtZone,
    distance_norm: distanceNorm,
    angle_deg: angleDeg,
    fps,
    resolution: { w: snap.w, h: snap.h },
    device: deviceLabel || navigator.userAgent.slice(0, 100),
  };

  try {
    const r = await api.post('/yolo/capture', body);
    return r.data || { ok: true };
  } catch (e) {
    // Never propagate — this is purely opt-in background work.
    return { ok: false, error: e?.response?.data?.detail || 'capture failed' };
  }
}
